import { useState, useEffect } from "react";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import "./GestionDesChambres.css";

const GestionDesChambres = () => {
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteRoom, setDeleteRoom] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");
  const [selectedRoomForDetails, setSelectedRoomForDetails] = useState(null);

  const [roomData, setRoomData] = useState({
    number: "",
    type: "",
    adults: 1,
    children: 1,
    description: "",
    status: "libre",
  });

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/rooms`);
      const data = await response.json();
      setRooms(data);
    } catch (err) {
      console.error("Erreur lors du chargement des chambres", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRoomData({ ...roomData, [name]: value });
  };

  const resetForm = () => {
    setRoomData({
      number: "",
      type: "",
      adults: 1,
      children: 1,
      description: "",
      status: "libre",
    });
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const url = editingRoom 
        ? `${process.env.REACT_APP_API_URL}/api/rooms/${roomData.number}`
        : `${process.env.REACT_APP_API_URL}/api/rooms`;
      
      const method = editingRoom ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(roomData),
      });

      if (response.ok) {
        
        const updatedRoom = await response.json();
        if (editingRoom) {
          setRooms(rooms.map(room => room.number === updatedRoom.number ? updatedRoom : room));
        } else {
          setRooms([...rooms, updatedRoom]);
        }
        setShowForm(false);
        resetForm();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.message || "Une erreur est survenue");
      }
    } catch (error) {
      console.error("Erreur:", error);
      setErrorMessage("Erreur de connexion au serveur");
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setRoomData(room);
    setShowForm(true);
  };

  const confirmDelete = (room) => {
    setDeleteRoom(room);
    setDeleteInput("");
  };

  const handleDelete = async () => {
    if (!deleteRoom || deleteInput !== `${deleteRoom.number}-${deleteRoom.type}`) {
      alert("Les informations ne correspondent pas !");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/rooms/${deleteRoom.number}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setRooms(rooms.filter(room => room.number !== deleteRoom.number));
        setDeleteRoom(null);
      } else {
        console.error("Erreur lors de la suppression !");
      }
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1 className="admin-title">Gestion des Chambres</h1>
        <div className="admin-actions">
          <button 
            className="btn btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingRoom(null);
              resetForm();
            }}
          >
            Ajouter une chambre
          </button>
        </div>
      </header>

      <div className="data-container">
        <div className="data-header">
          <h2 className="data-title">Liste des Chambres</h2>
        </div>
        
        <table className="data-table">
          <thead>
            <tr>
              <th>Numéro</th>
              <th>Type</th>
              <th>Capacité</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((room) => (
              <tr key={room.number}>
                <td>{room.number}</td>
                <td>{room.type}</td>
                <td>{room.adults} adultes, {room.children} enfants</td>
                <td>
                  <span className={`badge ${
                    room.status === 'libre' ? 'badge-success' : 
                    room.status === 'occupée' ? 'badge-danger' : 'badge-warning'
                  }`}>
                    {room.status}
                  </span>
                </td>
                <td>
                  <div className="data-actions">
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => setSelectedRoomForDetails(room)}
                    >
                      Détails
                    </button>
                    <button 
                      className="btn btn-warning btn-sm"
                      onClick={() => handleEdit(room)}
                    >
                      Modifier
                    </button>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => confirmDelete(room)}
                    >
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingRoom ? "Modifier la chambre" : "Ajouter une chambre"}
              </h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Numéro de chambre</label>
                  <input
                    className="form-control"
                    type="text"
                    name="number"
                    value={roomData.number}
                    onChange={handleChange}
                    required
                    disabled={!!editingRoom}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Type de chambre</label>
                  <input
                    className="form-control"
                    type="text"
                    name="type"
                    value={roomData.type}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nombre d'adultes</label>
                  <input
                    className="form-control"
                    type="number"
                    name="adults"
                    min="1"
                    value={roomData.adults}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nombre d'enfants</label>
                  <input
                    className="form-control"
                    type="number"
                    name="children"
                    min="0"
                    value={roomData.children}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={roomData.description}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-control"
                    name="status"
                    value={roomData.status}
                    onChange={handleChange}
                  >
                    <option value="libre">Libre</option>
                    <option value="occupée">Occupée</option>
                    <option value="en nettoyage">En nettoyage</option>
                  </select>
                </div>
                
                {errorMessage && (
                  <div className="alert alert-danger">{errorMessage}</div>
                )}
                
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingRoom ? "Enregistrer" : "Ajouter"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedRoomForDetails && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h3 className="modal-title">
                Détails de la chambre {selectedRoomForDetails.number}
              </h3>
              <button 
                className="modal-close"
                onClick={() => setSelectedRoomForDetails(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="room-details-grid">
                <div>
                  <h4>Informations</h4>
                  <p><strong>Type:</strong> {selectedRoomForDetails.type}</p>
                  <p><strong>Capacité:</strong> {selectedRoomForDetails.adults} adultes, {selectedRoomForDetails.children} enfants</p>
                  <p><strong>Statut:</strong> 
                    <span className={`badge ${
                      selectedRoomForDetails.status === 'libre' ? 'badge-success' : 
                      selectedRoomForDetails.status === 'occupée' ? 'badge-danger' : 'badge-warning'
                    }`}>
                      {selectedRoomForDetails.status}
                    </span>
                  </p>
                  <p><strong>Description:</strong> {selectedRoomForDetails.description}</p>
                </div>
                
                <div>
                  <h4>Disponibilité</h4>
                  <Calendar
                    className="custom-calendar"
                    tileClassName={({ date }) => {
                      const reserved = selectedRoomForDetails.reservedDates?.some(
                        range => date >= new Date(range.date_entree) && date <= new Date(range.date_sortie)
                      );
                      return reserved ? 'reserved-day' : null;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteRoom && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Confirmer la suppression</h3>
              <button 
                className="modal-close"
                onClick={() => setDeleteRoom(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>
                Vous êtes sur le point de supprimer la chambre <strong>{deleteRoom.number}</strong> ({deleteRoom.type}).
                Cette action est irréversible.
              </p>
              <p>
                Pour confirmer, veuillez taper "<strong>{deleteRoom.number}-{deleteRoom.type}</strong>" ci-dessous :
              </p>
              
              <div className="form-group">
                <input
                  className="form-control"
                  type="text"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder={`${deleteRoom.number}-${deleteRoom.type}`}
                />
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setDeleteRoom(null)}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={deleteInput !== `${deleteRoom.number}-${deleteRoom.type}`}
                >
                  Confirmer la suppression
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionDesChambres;